import type {
    ElementClarifyingQuestion,
    SlicerPromotionAnswer,
    SlicerPromotionItem,
    SlicerPromotionItemResult,
    SlicerPromotionQuestion,
} from 'api/element-issues';

export const SLICER_PROMOTION_TYPE = 'slicer_promotion';

/** The activating option value (SlicerGovernanceQuestionService::ANSWER_USE_ONE); retry re-submits it. */
export const SLICER_ANSWER_USE_ONE = 'use_one_at_a_time';

/**
 * True when the question renders as the structured slicer promotion control.
 * Gated on type alone, mirroring the measure-date rule: the legacy free-text
 * UI must never post plain text for this type (the backend expects a JSON
 * `answers` payload), so even an empty group routes to the structured control.
 */
export const isSlicerPromotionQuestion = (q: ElementClarifyingQuestion): boolean => q.type === SLICER_PROMOTION_TYPE;

/**
 * The typed view of a `slicer_promotion` question. The shared
 * `ElementClarifyingQuestion.items` field is typed for the measure-date
 * family; the backend discriminates grouped questions on `type`, so this cast
 * is safe exactly when `isSlicerPromotionQuestion` holds.
 */
export const asSlicerPromotionQuestion = (q: ElementClarifyingQuestion): SlicerPromotionQuestion | null =>
    isSlicerPromotionQuestion(q) ? (q as unknown as SlicerPromotionQuestion) : null;

/** In-progress choice for one item: the picked option value plus the reason text. */
export interface SlicerItemDraft {
    answer?: string;
    reason: string;
}

export const EMPTY_DRAFT: SlicerItemDraft = { reason: '' };

/** The option is only selectable while the backend still offers it on this item. */
export const offeredOption = (item: SlicerPromotionItem, value: string | undefined) =>
    value ? item.options.find((option) => option.value === value) : undefined;

/** True when the drafted choice demands a reason (Safe to combine, per the backend flag). */
export const reasonRequired = (item: SlicerPromotionItem, draft: SlicerItemDraft): boolean =>
    !!offeredOption(item, draft.answer)?.requiresReason;

/**
 * The next draft after picking an option. A reason typed under a
 * reason-requiring option must never survive a switch to an option that does
 * not require one: the text was written for a different decision and would
 * otherwise leak into that answer's audit trail.
 */
export const draftForSelection = (
    item: SlicerPromotionItem,
    previous: SlicerItemDraft,
    value: string,
): SlicerItemDraft => ({
    answer: value,
    reason: offeredOption(item, value)?.requiresReason ? previous.reason : '',
});

/**
 * Per-item submit gate: an offered option is picked, and when that option
 * requires a reason the reason is non-empty after trimming. The same rule is
 * enforced server-side (`reason_required`); gating here keeps the failure out
 * of the round trip.
 */
export const canSubmitItem = (item: SlicerPromotionItem, draft: SlicerItemDraft): boolean => {
    const option = offeredOption(item, draft.answer);
    if (!option) return false;
    return !option.requiresReason || draft.reason.trim() !== '';
};

/**
 * The `answer` string posted through the ordinary answer endpoint for ONE
 * item. The backend json-decodes it and applies each `answers` entry
 * independently (per-item CAS on itemBasisHash), so each item submits alone.
 * The reason rides along ONLY when the selected option requires one (and is
 * non-empty after trimming) — defense in depth on top of draftForSelection,
 * so a reason typed for a different choice can never enter the audit trail.
 */
export const buildSlicerItemAnswer = (item: SlicerPromotionItem, draft: SlicerItemDraft): string => {
    const entry: SlicerPromotionAnswer = {
        itemKey: item.itemKey,
        answer: draft.answer ?? '',
        itemBasisHash: item.itemBasisHash,
    };
    const reason = draft.reason.trim();
    if (reason && offeredOption(item, draft.answer)?.requiresReason) entry.reason = reason;
    return JSON.stringify({ answers: [entry] });
};

/** Retry of a half-completed activation: re-submit the activating answer under the same CAS token. */
export const buildRetryAnswer = (item: SlicerPromotionItem): string =>
    buildSlicerItemAnswer(item, { answer: SLICER_ANSWER_USE_ONE, reason: '' });

/**
 * The per-item result echoed for the submitted item key, when the response
 * carried a well-formed results array (the backend echoes the SUBMITTED
 * itemKey, so this stays correct even when the saga's candidate→proposed step
 * changes the item's derived key in the refreshed projection).
 */
export const pickItemResult = (results: unknown, itemKey: string): SlicerPromotionItemResult | null => {
    if (!Array.isArray(results)) return null;
    for (const entry of results as Partial<SlicerPromotionItemResult>[]) {
        if (entry && entry.itemKey === itemKey && typeof entry.status === 'string') {
            return entry as SlicerPromotionItemResult;
        }
    }
    return null;
};

export type ItemOutcomeKind = 'applied' | 'pending' | 'changed' | 'rejected';

export interface ItemOutcome {
    kind: ItemOutcomeKind;
    /** Steward-safe message built server-side; null when the backend sent none. */
    message: string | null;
}

/**
 * Classify a per-item result (the PRIMARY outcome signal; the refetched
 * projection only reconciles state afterwards):
 * - applied: the decision landed (the item leaves the derived set);
 * - pending: the activation saga did not finish — the durable row stays
 *   activation_pending and retry re-enters at step two (a step-one `refused`
 *   also leaves the pending row behind, so it retries the same way);
 * - changed: the item moved under the steward (stale CAS) or no longer exists;
 * - rejected: the submission itself was invalid for this item
 *   (reason_required / option_not_offered / activation_blocked — all gated
 *   client-side, kept as defense in depth).
 */
export const classifyItemResult = (result: SlicerPromotionItemResult): ItemOutcome => {
    const message = result.message ?? null;
    switch (result.status) {
        case 'answered':
        case 'deferred':
        case 'activated':
            return { kind: 'applied', message };
        case 'activation_pending':
        case 'refused':
            return { kind: 'pending', message };
        case 'stale_item':
        case 'unknown_item':
            return { kind: 'changed', message };
        default:
            return { kind: 'rejected', message };
    }
};

export type SubmissionOutcome = 'applied' | 'pending' | 'conflict';

/**
 * Fallback classification against the re-fetched projection, used only when
 * the response carried no readable per-item result: an applied answer removes
 * the item (answered, dismissed, deferred, or activated rows all leave the
 * derived set); a half-completed activation re-surfaces it in
 * `activation_pending`; anything else means the write did not land and the
 * steward must re-decide from the refreshed content.
 */
export const submissionOutcome = (
    submitted: { itemKey: string; itemBasisHash: string },
    items: SlicerPromotionItem[],
): SubmissionOutcome => {
    const item = items.find((candidate) => candidate.itemKey === submitted.itemKey);
    if (!item) return 'applied';
    if (item.state === 'activation_pending') return 'pending';
    return 'conflict';
};

/** Count of member values beyond the projected samples ("+N more"). */
export const hiddenMemberCount = (item: SlicerPromotionItem): number =>
    Math.max(0, item.memberCount - item.samples.length);
